/**
 * GoodsIntakeForm — public page, no auth required.
 * Accessible at /goods-intake
 *
 * Contract clients fill this in to tell us what materials they're sending
 * us. Creates a pending record in goods_intake_submissions for PLD staff
 * to review — nothing is created automatically. On approval, staff match
 * each line to a stock item (or create a new one) and Blake generates a
 * draft purchase order against a supplier record matching the client.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const GREEN  = '#22391f';
const YELLOW = '#FFDC06';

// ── Shared primitives (mirrors OutletRegister.jsx) ──────────────────────────
function Field({ label, required, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: '#2d2d2d' }}>
        {label}{required && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {hint && <span style={{ fontSize: 11, color: '#6b7280' }}>{hint}</span>}
    </div>
  );
}

const inputStyle = {
  padding: '9px 12px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: 14,
  background: '#fff',
  width: '100%',
  boxSizing: 'border-box',
  color: '#111',
  outline: 'none',
};

function Input(props) {
  return <input {...props} style={{ ...inputStyle, ...props.style }} />;
}

function Select({ children, ...props }) {
  return <select {...props} style={{ ...inputStyle, ...props.style }}>{children}</select>;
}

function Textarea(props) {
  return <textarea {...props} style={{ ...inputStyle, minHeight: 80, resize: 'vertical', ...props.style }} />;
}

function SectionHeader({ number, title, subtitle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, margin: '32px 0 20px' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: GREEN, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{number}</div>
      <div>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: GREEN }}>{title}</h3>
        {subtitle && <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>{subtitle}</p>}
      </div>
    </div>
  );
}

// ── Invalid link screen ─────────────────────────────────────────────────────
function InvalidLinkScreen({ message }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, margin: '0 auto 20px' }}>!</div>
      <h2 style={{ margin: '0 0 10px', fontSize: 22, fontWeight: 700, color: GREEN }}>This link isn't valid</h2>
      <p style={{ margin: '0 0 8px', fontSize: 15, color: '#374151', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
        {message || "This goods intake link has expired or been revoked. Please check with Pleasant Land Distillery for your current link."}
      </p>
      <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
        Questions? Email us at <a href="mailto:hello@pleasantland.co.uk" style={{ color: GREEN }}>hello@pleasantland.co.uk</a>
      </p>
    </div>
  );
}

// ── Success screen ────────────────────────────────────────────────────────────
function SuccessScreen() {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, margin: '0 auto 20px' }}>✓</div>
      <h2 style={{ margin: '0 0 10px', fontSize: 22, fontWeight: 700, color: GREEN }}>Thank you!</h2>
      <p style={{ margin: '0 0 8px', fontSize: 15, color: '#374151', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
        Your goods intake details have been received. A member of the Pleasant Land Distillery team will review them shortly.
      </p>
      <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
        Questions? Email us at <a href="mailto:hello@pleasantland.co.uk" style={{ color: GREEN }}>hello@pleasantland.co.uk</a>
      </p>
    </div>
  );
}

const BLANK_LINE = {
  material_name: '', quantity: '', unit: '', pack_size: '',
  dimensions: '', supplier_stock_code: '', batch_number: '', best_before_date: '',
};

const BLANK = {
  submitted_by_name: '',
  submitted_by_email: '',
  submitted_by_phone: '',
  delivery_location: '',
  expected_delivery_date: '',
  notes: '',
};

export default function GoodsIntakeForm() {
  const { token } = useParams();
  const [clientName, setClientName]   = useState(null);
  const [linkError, setLinkError]     = useState('');
  const [resolving, setResolving]     = useState(true);
  const [form, setForm]           = useState(BLANK);
  const [lines, setLines]         = useState([{ ...BLANK_LINE }]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) { setLinkError("This link is missing its code — please use the full link Pleasant Land Distillery sent you."); setResolving(false); return; }
    fetch(`/api/public/goods-intake/resolve/${token}`)
      .then(async (r) => {
        const json = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(json.error || 'Invalid link');
        setClientName(json.client_name);
      })
      .catch((err) => setLinkError(err.message))
      .finally(() => setResolving(false));
  }, [token]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines(ls => [...ls, { ...BLANK_LINE }]);
  const removeLine = (i) => setLines(ls => ls.length === 1 ? ls : ls.filter((_, idx) => idx !== i));

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const cleanLines = lines.filter(l => l.material_name.trim());
      if (cleanLines.length === 0) throw new Error('Please add at least one item');

      const res = await fetch('/api/public/goods-intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, token, lines: cleanLines }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || json.message || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* Header */}
      <div style={{ background: GREEN, padding: '0 24px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto', padding: '20px 0', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: YELLOW, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 16, color: GREEN, flexShrink: 0 }}>PL</div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>Pleasant Land Distillery</div>
            <div style={{ color: 'rgba(255,255,255,.65)', fontSize: 12 }}>Goods Intake</div>
          </div>
        </div>
      </div>

      {/* Yellow accent bar */}
      <div style={{ height: 4, background: YELLOW }} />

      {/* Card */}
      <div style={{ maxWidth: 820, margin: '32px auto', padding: '0 16px 48px' }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.1)', overflow: 'hidden' }}>

          {/* Card header */}
          <div style={{ padding: '28px 32px 20px', borderBottom: '1px solid #e5e7eb' }}>
            <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: GREEN }}>Goods intake</h1>
            <p style={{ margin: 0, fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>
              Please tell us what you're delivering to us, as far in advance as you can. We'll review this and confirm before your delivery arrives.
            </p>
          </div>

          {resolving ? (
            <p style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Loading…</p>
          ) : linkError ? (
            <InvalidLinkScreen message={linkError} />
          ) : submitted ? (
            <SuccessScreen />
          ) : (
            <form onSubmit={handleSubmit} style={{ padding: '8px 32px 32px' }}>

              {/* 1. Your details */}
              <SectionHeader number={1} title="Your details" subtitle="The person completing this form" />
              <div style={{ display: 'grid', gap: 14 }}>
                <Field label="Your name" required>
                  <Input value={form.submitted_by_name} onChange={e => set('submitted_by_name', e.target.value)} required autoFocus placeholder="Jane Smith" />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Your email" required>
                    <Input type="email" value={form.submitted_by_email} onChange={e => set('submitted_by_email', e.target.value)} required placeholder="jane@example.com" />
                  </Field>
                  <Field label="Your phone">
                    <Input type="tel" value={form.submitted_by_phone} onChange={e => set('submitted_by_phone', e.target.value)} placeholder="07700 900000" />
                  </Field>
                </div>
              </div>

              {/* 2. Delivery */}
              <SectionHeader number={2} title="Delivery details" />
              <div style={{ display: 'grid', gap: 14 }}>
                <Field label="Submitting as">
                  <div style={{ ...inputStyle, background: '#f3f4f6', fontWeight: 600, color: GREEN }}>{clientName}</div>
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Delivery stock location" hint="Warehouse or site, if known">
                    <Input value={form.delivery_location} onChange={e => set('delivery_location', e.target.value)} placeholder="Warehouse" />
                  </Field>
                  <Field label="Expected delivery date">
                    <Input type="date" value={form.expected_delivery_date} onChange={e => set('expected_delivery_date', e.target.value)} />
                  </Field>
                </div>
              </div>

              {/* 3. Items */}
              <SectionHeader number={3} title="Items" subtitle="Add every product or material in this delivery" />
              <div style={{ display: 'grid', gap: 16 }}>
                {lines.map((l, i) => (
                  <div key={i} style={{ padding: 16, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa', position: 'relative' }}>
                    {lines.length > 1 && (
                      <button type="button" onClick={() => removeLine(i)}
                        style={{ position: 'absolute', top: 10, right: 10, border: 'none', background: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
                        aria-label="Remove item">×</button>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <Field label="Product / material name" required>
                        <Input value={l.material_name} onChange={e => setLine(i, 'material_name', e.target.value)} placeholder="e.g. Cassia Bark Chips" />
                      </Field>
                      <Field label="Quantity">
                        <Input value={l.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} placeholder="e.g. 25" />
                      </Field>
                      <Field label="Unit">
                        <Input value={l.unit} onChange={e => setLine(i, 'unit', e.target.value)} placeholder="kg, litres, units..." />
                      </Field>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                      <Field label="Pack size">
                        <Input value={l.pack_size} onChange={e => setLine(i, 'pack_size', e.target.value)} placeholder="e.g. 5kg bags" />
                      </Field>
                      <Field label="Dimensions">
                        <Input value={l.dimensions} onChange={e => setLine(i, 'dimensions', e.target.value)} />
                      </Field>
                      <Field label="Supplier stock code">
                        <Input value={l.supplier_stock_code} onChange={e => setLine(i, 'supplier_stock_code', e.target.value)} />
                      </Field>
                      <Field label="Batch number">
                        <Input value={l.batch_number} onChange={e => setLine(i, 'batch_number', e.target.value)} />
                      </Field>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <Field label="Best before date" hint="If batch number isn't supplied, the delivery date will be used instead">
                        <Input type="date" value={l.best_before_date} onChange={e => setLine(i, 'best_before_date', e.target.value)} style={{ maxWidth: 220 }} />
                      </Field>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addLine}
                  style={{ padding: '10px 0', borderRadius: 8, border: `1px dashed ${GREEN}`, background: '#fff', color: GREEN, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  + Add another item
                </button>
              </div>

              {/* 4. Notes */}
              <SectionHeader number={4} title="Anything else?" />
              <Field label="Notes" hint="Special handling, access requirements, etc.">
                <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} />
              </Field>

              {/* Delivery info note, from the paper form */}
              <div style={{ margin: '24px 0', padding: '14px 16px', background: '#f0fdf4', borderRadius: 8, borderLeft: `3px solid ${GREEN}`, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                Operating hours for deliveries are 09:00–17:00 Monday–Friday. Access is available for HGV transport, including articulated lorries.
                Delivery contacts: Operations, 07471 329575.
              </div>

              {error && (
                <div style={{ margin: '0 0 16px', padding: '12px 16px', background: '#fee2e2', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                style={{
                  width: '100%', padding: '13px 0', borderRadius: 8, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                  background: saving ? '#6b7280' : GREEN, color: '#fff', fontSize: 15, fontWeight: 700,
                  transition: 'background .15s',
                }}
              >
                {saving ? 'Submitting…' : 'Submit goods intake'}
              </button>
            </form>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: '#9ca3af' }}>
          © {new Date().getFullYear()} Pleasant Land Distillery Ltd · Est. 2022
        </p>
      </div>
    </div>
  );
}
